import { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { OnboardingDialog } from '@/components/OnboardingDialog';
import { SensitiveDataAlert } from '@/components/SensitiveDataAlert';
import { Loader2, ChevronDown, MessageSquarePlus } from 'lucide-react';
import { useAISettings } from '@/hooks/useAISettings';
import { useFS } from '@/hooks/useFS';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useKeepAlive } from '@/hooks/useKeepAlive';
import { useAppContext } from '@/hooks/useAppContext';
import { useAIChat } from '@/hooks/useAIChat';
import { useProviderModels } from '@/hooks/useProviderModels';
import { AIMessageItem } from '@/components/AIMessageItem';
import { AIErrorMessage } from '@/components/AIErrorMessage';
import { TextEditorViewTool } from '@/lib/tools/TextEditorViewTool';
import { TextEditorWriteTool } from '@/lib/tools/TextEditorWriteTool';
import { TextEditorStrReplaceTool } from '@/lib/tools/TextEditorStrReplaceTool';
import { NpmAddPackageTool } from '@/lib/tools/NpmAddPackageTool';
import { NpmRemovePackageTool } from '@/lib/tools/NpmRemovePackageTool';
import { GitCommitTool } from '@/lib/tools/GitCommitTool';
import { BuildProjectTool } from '@/lib/tools/BuildProjectTool';
import { DeployProjectTool } from '@/lib/tools/DeployProjectTool';
import { NostrReadNipTool } from '@/lib/tools/NostrReadNipTool';
import { NostrFetchEventTool } from '@/lib/tools/NostrFetchEventTool';
import { NostrReadKindTool } from '@/lib/tools/NostrReadKindTool';
import { NostrReadTagTool } from '@/lib/tools/NostrReadTagTool';
import { NostrReadProtocolTool } from '@/lib/tools/NostrReadProtocolTool';
import { NostrReadNipsIndexTool } from '@/lib/tools/NostrReadNipsIndexTool';
import { NostrGenerateKindTool } from '@/lib/tools/NostrGenerateKindTool';
import { ShellTool } from '@/lib/tools/ShellTool';
import { TypecheckTool } from '@/lib/tools/TypecheckTool';
import { ReadConsoleMessagesTool } from '@/lib/tools/ReadConsoleMessagesTool';
import { StripeSetupTool } from '@/lib/tools/StripeSetupTool';
import { createSkillTool } from '@/lib/tools/SkillTool';
import { ProjectPreviewConsoleError } from '@/lib/consoleMessages';
import { toolToOpenAI } from '@/lib/tools/openai-adapter';
import { Tool } from '@/lib/tools/Tool';
import OpenAI from 'openai';
import { saveFileToTmp } from '@/lib/fileUtils';
import { detectSensitiveData } from '@/lib/security';
import { useGit } from '@/hooks/useGit';
import { useSkills } from '@/hooks/useSkills';
import { useIntegrations } from '@/hooks/useIntegrations';
import { ShakespeareLogo } from '@/components/ShakespeareLogo';
import { ChatInput, type ChatMode } from '@/components/Shakespeare/ChatInput';

// Clean interfaces now handled by proper hooks

interface ChatPaneProps {
  projectId: string;
  onNewChat: () => void;
  onFirstInteraction?: () => void;
  onLoadingChange?: (isLoading: boolean) => void;
  isLoading?: boolean;
  isBuildLoading?: boolean;
  consoleError?: ProjectPreviewConsoleError | null;
  onDismissConsoleError?: () => void;
}

export interface ChatPaneRef {
  startNewSession: () => void;
  sendErrorFixRequest: (errorMessage: string) => void;
}

export const ChatPane = forwardRef<ChatPaneRef, ChatPaneProps>(({
  projectId,
  onNewChat,
  onFirstInteraction,
  onLoadingChange,
  isLoading: externalIsLoading,
  isBuildLoading: externalIsBuildLoading,
  consoleError,
  onDismissConsoleError,
}, ref) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fs } = useFS();
  const { git } = useGit();
  const { user } = useCurrentUser();
  const { models } = useProviderModels();
  const { config } = useAppContext();
  const { getSkillByName, getEnabledSkills } = useSkills();
  const { getIntegrationsByType } = useIntegrations();
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [scrolledProjects] = useState(() => new Set<string>());
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const [aiError, setAIError] = useState<Error | null>(null);

  // Use external state if provided, otherwise default to false
  const isBuildLoading = externalIsBuildLoading || false;
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { isConfigured, settings, addRecentlyUsedModel, isLoading: isLoadingSettings } = useAISettings();
  const [providerModel, setProviderModel] = useState(() => {
    // Initialize with first recently used model if available, otherwise empty
    return settings.recentlyUsedModels?.[0] || '';
  });
  // State to control model selector dropdown
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const [shouldAutostart, setShouldAutostart] = useState(false);
  const autostartedRef = useRef(false);
  const [chatMode, setChatMode] = useState<ChatMode>('act');

  // Sensitive data detection state
  const [showSensitiveDataAlert, setShowSensitiveDataAlert] = useState(false);
  const [sensitiveDataType, setSensitiveDataType] = useState<'stripe' | 'generic'>('stripe');
  const [sensitiveKeyType, setSensitiveKeyType] = useState<'live' | 'test' | 'restricted'>('test');
  const pendingMessageRef = useRef<{ input: string; files: File[] } | null>(null);

  // Memoize model selector onChange handler to prevent unnecessary re-renders
  const handleModelChange = useCallback((newModel: string) => {
    setProviderModel(newModel);
  }, []);

  useEffect(() => {
    if (!providerModel && settings.recentlyUsedModels?.length) {
      setProviderModel(settings.recentlyUsedModels[0]);
    }
  }, [providerModel, settings.recentlyUsedModels]);

  useEffect(() => {
    const urlModel = searchParams.get('model');
    const autostart = searchParams.get('autostart');

    if (urlModel && !providerModel) {
      setProviderModel(urlModel);
    }
    if (autostart === 'true') {
      setShouldAutostart(true);
    }

    // Clear the autostart and model parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('autostart');
    newSearchParams.delete('model');
    setSearchParams(newSearchParams, { replace: true });
  }, [providerModel, searchParams, setSearchParams])



  // Initialize AI chat with tools
  const cwd = `/projects/${projectId}`;
  const esmUrlRef = useRef(config.esmUrl);
  const customTools = useMemo(() => {
    const baseTools = {
      git_commit: new GitCommitTool(fs, cwd, git),
      text_editor_view: new TextEditorViewTool(fs, cwd),
      text_editor_write: new TextEditorWriteTool(fs, cwd),
      text_editor_str_replace: new TextEditorStrReplaceTool(fs, cwd),
      npm_add_package: new NpmAddPackageTool(fs, cwd),
      npm_remove_package: new NpmRemovePackageTool(fs, cwd),
      build_project: new BuildProjectTool(fs, cwd, esmUrlRef.current),
      typecheck: new TypecheckTool(fs, cwd),
      nostr_read_nip: new NostrReadNipTool(),
      nostr_fetch_event: new NostrFetchEventTool(),
      nostr_read_kind: new NostrReadKindTool(),
      nostr_read_tag: new NostrReadTagTool(),
      nostr_read_protocol: new NostrReadProtocolTool(),
      nostr_read_nips_index: new NostrReadNipsIndexTool(),
      nostr_generate_kind: new NostrGenerateKindTool(),
      shell: new ShellTool(fs, cwd, git),
      read_console_messages: new ReadConsoleMessagesTool(),
      skill: createSkillTool({ getSkillByName, getEnabledSkills }),
      stripe_setup: new StripeSetupTool(fs, () => {
        const stripeIntegrations = getIntegrationsByType('stripe');
        return stripeIntegrations[0] || null;
      }),
    };

    // Add deploy tool only if user is logged in
    if (user && user.signer) {
      return {
        ...baseTools,
        deploy_project: new DeployProjectTool(fs, cwd, user.signer, projectId),
      };
    }

    return baseTools;
  }, [fs, git, cwd, user, projectId, getSkillByName, getEnabledSkills, getIntegrationsByType]);

  // Convert tools to OpenAI format
  const tools = useMemo(() => {
    const result: Record<string, OpenAI.Chat.Completions.ChatCompletionTool> = {};
    for (const [name, tool] of Object.entries(customTools)) {
      result[name] = toolToOpenAI(name, tool as Tool<unknown>);
    }
    return result;
  }, [customTools]);

  // Keep-alive functionality to prevent tab throttling during AI processing
  const { updateMetadata } = useKeepAlive({
    enabled: externalIsLoading || isBuildLoading,
    title: 'AfterHours',
    artist: `Working on ${projectId}...`,
    artwork: [
      {
        src: '/shakespeare-512x512.png',
        sizes: '512x512',
        type: 'image/png'
      }
    ]
  });

  // Memoize the metadata update callback to avoid unnecessary re-renders
  const onUpdateMetadata = useCallback((title: string, description: string) => {
    updateMetadata(title, description);
  }, [updateMetadata]);

  // Handle AI errors
  const handleAIError = useCallback((error: Error) => {
    setAIError(error);
  }, []);

  const {
    messages,
    streamingMessage,
    isLoading: internalIsLoading,
    totalCost,
    lastInputTokens,
    addMessage,
    sendMessage,
    startGeneration,
    stopGeneration,
    startNewSession: internalStartNewSession,
  } = useAIChat({
    projectId,
    tools,
    customTools,
    onUpdateMetadata,
    onAIError: handleAIError,
  });

  // Use external loading state if provided, otherwise use internal state
  const isLoading = externalIsLoading !== undefined ? externalIsLoading : internalIsLoading;

  // Calculate context usage percentage
  const currentModel = useMemo(() => {
    if (!providerModel.trim()) return null;
    return models.find(model => model.fullId === providerModel.trim());
  }, [models, providerModel]);

  const contextUsagePercentage = useMemo(() => {
    if (!currentModel?.contextLength || !lastInputTokens) return 0;
    return Math.min((lastInputTokens / currentModel.contextLength) * 100, 100);
  }, [currentModel, lastInputTokens]);

  // Notify parent of loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(internalIsLoading);
    }
  }, [internalIsLoading, onLoadingChange]);

  // Timer to detect when AI appears stuck during tool generation
  useEffect(() => {
    // Start a 2-second timer
    const timer = streamingMessage
      ? setTimeout(() => setIsStuck(true), 2000)
      : undefined;

    return () => {
      setIsStuck(false);
      clearTimeout(timer);
    };
  }, [streamingMessage]);

  // Function to scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, []);

  // Function to open model selector dropdown
  const openModelSelector = useCallback(() => {
    setIsModelSelectorOpen(true);
  }, []);

  // Check for autostart parameter and trigger AI generation
  useEffect(() => {
    if (autostartedRef.current) return;
    // Wait for settings to load before checking configuration
    if (isLoadingSettings) return;

    if (shouldAutostart && providerModel && isConfigured) {
      // Start AI generation
      addRecentlyUsedModel(providerModel);
      startGeneration(providerModel, chatMode);
      autostartedRef.current = true;
    }
  }, [addRecentlyUsedModel, isConfigured, isLoadingSettings, providerModel, shouldAutostart, startGeneration, chatMode]);

  // Simple scroll event listener
  useEffect(() => {
    const container = scrollAreaRef.current;
    if (!container) return;

    const handleScroll = () => {
      const threshold = 100;
      const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
      const hasScrollableContent = container.scrollHeight > container.clientHeight;

      setShowScrollToBottom(!isNearBottom && hasScrollableContent);
    };

    container.addEventListener('scroll', handleScroll);

    // Check immediately
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [messages]); // Re-run when messages change to ensure proper setup

  useEffect(() => {
    if (scrollAreaRef.current && (messages || streamingMessage)) {
      // Check if user was already at or near the bottom (within 100px threshold)
      const threshold = 100;
      const container = scrollAreaRef.current;
      const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;

      // Only auto-scroll if user was already near the bottom
      if (isNearBottom) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages, streamingMessage, isLoading]);

  // Scroll to bottom when first visiting a project (including page refresh)
  useEffect(() => {
    if (projectId && !scrolledProjects.has(projectId)) {
      // Small delay to ensure messages have loaded
      const timer = setTimeout(() => {
        scrollToBottom();
        scrolledProjects.add(projectId);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [projectId, scrolledProjects, scrollToBottom]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) {
      setIsDragOver(true);
    }
  }, [isDragOver]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only reset drag state if we're actually leaving the container
    // This prevents flickering when dragging over child elements
    const container = e.currentTarget;
    const relatedTarget = e.relatedTarget as Node;

    if (!container.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleSend = useCallback(async (input: string, attachedFiles: File[]) => {
    if ((!input.trim() && attachedFiles.length === 0) || isLoading) return;

    // Wait for settings to load before checking configuration
    if (isLoadingSettings) return;

    // If AI is not configured, show onboarding dialog
    if (!isConfigured) {
      setShowOnboarding(true);
      return;
    }

    // If configured but no model selected, don't proceed
    if (!providerModel.trim()) return;

    // Check for sensitive data in the input
    if (input.trim()) {
      const sensitiveDataResult = detectSensitiveData(input);

      if (sensitiveDataResult.hasSensitiveData) {
        // Store the pending message to send if user proceeds anyway
        pendingMessageRef.current = { input, files: attachedFiles };

        // Configure alert based on what was detected
        if (sensitiveDataResult.stripeKey) {
          setSensitiveDataType('stripe');
          setSensitiveKeyType(sensitiveDataResult.stripeKey.type);
        } else {
          setSensitiveDataType('generic');
        }

        // Show the warning dialog
        setShowSensitiveDataAlert(true);

        // Block the message from being sent
        return;
      }
    }

    const modelToUse = providerModel.trim();

    // Build message content as text parts
    const contentParts: Array<OpenAI.Chat.Completions.ChatCompletionContentPartText> = [];

    // Add user input as text part if present
    if (input.trim()) {
      contentParts.push({
        type: 'text',
        text: input.trim()
      });
    }

    // Process attached files and add as separate text parts
    if (attachedFiles.length > 0) {
      const filePromises = attachedFiles.map(async (file) => {
        try {
          const savedPath = await saveFileToTmp(fs, file);
          return `Added file: ${savedPath}`;
        } catch (error) {
          console.error('Failed to save file:', error);
          return `Failed to save file: ${file.name}`;
        }
      });

      const fileResults = await Promise.all(filePromises);

      // Add each file as a separate text part
      fileResults.forEach(fileResult => {
        contentParts.push({
          type: 'text',
          text: fileResult
        });
      });
    }

    // Add model to recently used when sending a message
    addRecentlyUsedModel(modelToUse);

    // Send as text parts if we have multiple parts, otherwise send as string for simplicity
    const messageContent = contentParts.length === 1 ? contentParts[0].text : contentParts;
    await sendMessage(messageContent, modelToUse, chatMode);
  }, [addRecentlyUsedModel, fs, isConfigured, isLoading, isLoadingSettings, providerModel, sendMessage, chatMode]);

  // Handle textarea focus - show onboarding if not configured
  const handleTextareaFocus = useCallback(() => {
    // Don't show onboarding while settings are still loading
    if (!isLoadingSettings && !isConfigured) {
      setShowOnboarding(true);
    }
    if (onFirstInteraction) {
      onFirstInteraction();
    }
  }, [isConfigured, isLoadingSettings, onFirstInteraction]);

  // Handle first user interaction to enable audio context
  const handleFirstInteraction = useCallback(() => {
    // This will be handled automatically by the useKeepAlive hook
    // when isLoading becomes true after user interaction
    if (onFirstInteraction) {
      onFirstInteraction();
    }
  }, [onFirstInteraction]);

  // Handle user choosing to proceed with sending sensitive data anyway (non-live keys only)
  const handleProceedWithSensitiveData = useCallback(async () => {
    const pending = pendingMessageRef.current;
    if (!pending) return;

    // Clear the pending message
    pendingMessageRef.current = null;

    const { input, files } = pending;
    const modelToUse = providerModel.trim();

    // Build message content as text parts
    const contentParts: Array<OpenAI.Chat.Completions.ChatCompletionContentPartText> = [];

    // Add user input as text part if present
    if (input.trim()) {
      contentParts.push({
        type: 'text',
        text: input.trim()
      });
    }

    // Process attached files and add as separate text parts
    if (files.length > 0) {
      const filePromises = files.map(async (file) => {
        try {
          const savedPath = await saveFileToTmp(fs, file);
          return `Added file: ${savedPath}`;
        } catch (error) {
          console.error('Failed to save file:', error);
          return `Failed to save file: ${file.name}`;
        }
      });

      const fileResults = await Promise.all(filePromises);

      // Add each file as a separate text part
      fileResults.forEach(fileResult => {
        contentParts.push({
          type: 'text',
          text: fileResult
        });
      });
    }

    // Add model to recently used when sending a message
    addRecentlyUsedModel(modelToUse);

    // Send as text parts if we have multiple parts, otherwise send as string for simplicity
    const messageContent = contentParts.length === 1 ? contentParts[0].text : contentParts;
    await sendMessage(messageContent, modelToUse, chatMode);
  }, [addRecentlyUsedModel, chatMode, fs, providerModel, sendMessage]);

  // Clear error when project changes
  useEffect(() => {
    setAIError(null);
  }, [projectId]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    startNewSession: () => {
      setAIError(null);
      internalStartNewSession();
    },
    sendErrorFixRequest: (errorMessage: string) => {
      if (!providerModel.trim() || !isConfigured) return;

      const fixMessage = `I'm seeing this error in my project. Can you help me fix it?\n\n\`\`\`\n${errorMessage}\n\`\`\``;
      sendMessage(fixMessage, providerModel, chatMode);
    }
  }), [internalStartNewSession, providerModel, isConfigured, sendMessage, chatMode]);

  return (
    <div className="h-full flex flex-col relative">

      <div className="flex-1 overflow-y-scroll overflow-x-hidden" ref={scrollAreaRef}>
        <div className="p-4 space-y-4">
          {/* Empty state when no messages and not loading */}
          {messages.length === 0 && !streamingMessage && !isLoading && (
            <div className="flex-1 flex items-center justify-center min-h-[400px] px-4">
              <div className="w-full max-w-lg space-y-4">
                {/* Chat Modes Card */}
                <div className="rounded-lg border bg-card p-4 shadow-sm">
                  <h4 className="text-sm font-medium mb-3 text-foreground">{t('chatModes')}</h4>
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary">{t('act')}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{t('actModeTitle')}</p>
                        <p className="text-xs text-muted-foreground">{t('actModeDescription')}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-md bg-muted flex items-center justify-center">
                        <span className="text-xs font-semibold text-muted-foreground">{t('ask')}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{t('askModeTitle')}</p>
                        <p className="text-xs text-muted-foreground">{t('askModeDescription')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Capabilities Card */}
                <div className="rounded-lg border bg-card p-4 shadow-sm">
                  <h4 className="text-sm font-medium mb-3 text-foreground">{t('whatCanIDo')}</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1 flex-shrink-0">•</span>
                      <span>{t('capabilityFeatures')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1 flex-shrink-0">•</span>
                      <span>{t('capabilityEdit')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1 flex-shrink-0">•</span>
                      <span>{t('capabilityDebug')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-1 flex-shrink-0">•</span>
                      <span>{t('capabilityDeploy')}</span>
                    </li>
                  </ul>
                </div>

                {/* Skills Card */}
                <a
                  href="/settings/ai#plugins"
                  className="block rounded-lg border bg-card p-4 shadow-sm hover:bg-accent/50 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-foreground">{t('extendWithSkills')}</h4>
                      <p className="text-xs text-muted-foreground mt-1">{t('skillsDescription')}</p>
                    </div>
                    <span className="text-muted-foreground group-hover:text-foreground transition-colors">→</span>
                  </div>
                </a>
              </div>
            </div>
          )}

          {messages.map((message, index) => {
            // Find the corresponding tool call for tool messages
            let toolCall: OpenAI.Chat.Completions.ChatCompletionMessageToolCall | undefined = undefined;
            if (message.role === 'tool') {
              // Look backwards to find the assistant message with matching tool call
              for (let i = index - 1; i >= 0; i--) {
                const prevMessage = messages[i];
                if (prevMessage.role === 'assistant' && 'tool_calls' in prevMessage && prevMessage.tool_calls) {
                  toolCall = prevMessage.tool_calls.find(tc => tc.id === message.tool_call_id);
                  if (toolCall) break;
                }
              }
            }

            return (
              <AIMessageItem
                key={`${index}-${message.role}-${typeof message.content === 'string' ? message.content.slice(0, 50) : 'content'}`}
                message={message}
                toolCall={toolCall}
              />
            );
          })}
          {streamingMessage && (
            streamingMessage.content || streamingMessage.reasoning_content ? (
              <>
                <AIMessageItem
                  key="streaming-message"
                  message={streamingMessage}
                  isCurrentlyLoading={isLoading}
                />
                {/* Show loading skeleton if AI appears stuck generating tools */}
                {isStuck && (
                  <div key="stuck-loading-skeleton" className="flex">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              !(streamingMessage?.tool_calls?.[0]?.type === 'function') && (
                <div key="streaming-loading" className="flex">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                </div>
              )
            )
          )}
          {streamingMessage?.tool_calls?.[0]?.type === 'function' && (
            <div key="tool-calls-loading" className="-mt-2">
              <div className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin flex-shrink-0" />
                <span className="font-medium">
                  {t('running')} {streamingMessage.tool_calls?.[0]?.function?.name || 'tool'}...
                </span>
              </div>
            </div>
          )}

          {/* AI Error Message */}
          {aiError && (
            <AIErrorMessage
              error={aiError}
              onDismiss={() => setAIError(null)}
              onNewChat={() => {
                setAIError(null);
                onNewChat();
              }}
              onOpenModelSelector={openModelSelector}
              onTryAgain={() => {
                setAIError(null);
                if (providerModel) {
                  startGeneration(providerModel, chatMode);
                }
              }}
              onFixError={(error) => {
                setAIError(null);
                const fixMessage = `I encountered this error:\n\n${error.message}\n\nPlease analyze what went wrong and fix it.`;
                sendMessage(fixMessage, providerModel, chatMode);
              }}
              onExplainError={(error) => {
                setAIError(null);
                const explainMessage = `I encountered this error:\n\n${error.message}\n\nPlease explain what this error means and what might have caused it.`;
                sendMessage(explainMessage, providerModel, chatMode);
              }}
              providerModel={providerModel}
            />
          )}

          {/* Console Error Message */}
          {consoleError && !aiError && (
            <AIErrorMessage
              error={consoleError}
              onDismiss={() => onDismissConsoleError?.()}
              onNewChat={onNewChat}
              onOpenModelSelector={openModelSelector}
              onRequestConsoleErrorHelp={(error) => {
                // Send a message asking for help with console errors
                const errorMessages = error.errors.map(e => `[${e.level}] ${e.message}`).join('\n');
                const helpMessage = `I'm seeing these console errors in my project preview:\n\n${errorMessages}\n\nCan you help me fix them?`;
                sendMessage(helpMessage, providerModel, chatMode);
                onDismissConsoleError?.();
              }}
              providerModel={providerModel}
            />
          )}

          {/* Start new chat button - show when chat finishes (not loading and has messages) */}
          {!isLoading && messages.length > 0 && !aiError && (
            <div className="pt-2">
              <Button
                onClick={onNewChat}
                variant="outline"
                size="sm"
                className="w-full gap-2"
              >
                <MessageSquarePlus className="h-4 w-4" />
                Start new chat
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Scroll to bottom button */}
      {showScrollToBottom && (
        <div className="absolute bottom-36 left-1/2 -translate-x-1/2 z-10">
          <Button
            onClick={scrollToBottom}
            size="sm"
            variant="secondary"
            className="h-10 w-10 rounded-full shadow-lg border bg-background/80 backdrop-blur-sm hover:bg-background/90 transition-all duration-200"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      )}

      <ChatInput
        isLoading={isLoading}
        isConfigured={isConfigured}
        providerModel={providerModel}
        onProviderModelChange={handleModelChange}
        onSend={handleSend}
        onStop={stopGeneration}
        onFocus={handleTextareaFocus}
        onFirstInteraction={handleFirstInteraction}
        isModelSelectorOpen={isModelSelectorOpen}
        onModelSelectorOpenChange={setIsModelSelectorOpen}
        contextUsagePercentage={contextUsagePercentage}
        currentModelContextLength={currentModel?.contextLength}
        lastInputTokens={lastInputTokens}
        totalCost={totalCost}
        isDragOver={isDragOver}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        chatMode={chatMode}
        onChatModeChange={setChatMode}
      />

      {/* Onboarding Dialog */}
      <OnboardingDialog
        open={showOnboarding}
        onOpenChange={setShowOnboarding}
      />

      {/* Sensitive Data Alert */}
      <SensitiveDataAlert
        open={showSensitiveDataAlert}
        onOpenChange={(open) => {
          setShowSensitiveDataAlert(open);
          // Clear pending message when dialog is closed without proceeding
          if (!open) {
            pendingMessageRef.current = null;
          }
        }}
        dataType={sensitiveDataType}
        keyType={sensitiveKeyType}
        onProceedAnyway={sensitiveKeyType !== 'live' ? handleProceedWithSensitiveData : undefined}
      />
    </div>
  );
});
